import { AuthenticationService } from '../services/authentication.service';
import { Observable, Subject, throwError, catchError, switchMap, take, finalize } from 'rxjs';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { LoginResultModel } from '../models/api/login-result.model';
import { isPlatformBrowser } from '@angular/common';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshedToken$ = new Subject<string | null>();

  constructor(private readonly authenticationService: AuthenticationService,
    @Inject(PLATFORM_ID) private readonly platformId: string) {
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    const token = this.authenticationService.getToken();
    if (token) {
      request = this.addToken(request, token);
    }

    // Handle response
    return next.handle(request).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401Error(request, next);
        }
        else {
          return throwError(() => error);
        }
      }));
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) : Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      // Cancel previous pending requests
      this.refreshedToken$.next(null);

      return this.authenticationService.refreshToken().pipe(
        switchMap((result: LoginResultModel | null) => {
          return throwError(() => new Error('Logging out because token expired.'));
          /*
          if (result?.token != null) {
            // Continue requests waiting for new token
            this.refreshedToken$.next(result.token);

            return next.handle(this.addToken(request, result?.token));
          }
          else {
            // Return error and go into catchError to redirect
            return throwError(() => new Error('No token received.'));
          }*/
        }),
        catchError(error => {
          // Navigate to login page if refresh failed
          this.authenticationService.logout();

          // Prevent endless loop on server side
          if (isPlatformBrowser(this.platformId)) {        
            this.authenticationService.navigateToLogin();
          }

          // Cancel pending requests
          this.refreshedToken$.next(null);

          return throwError(() => error);
        }),
        finalize(() => this.isRefreshing = false));
    }
    else {
      // Let other requests wait on the refreshed token
      return this.refreshedToken$.pipe(
        take(1),
        switchMap(token => {
          if (token) {
            return next.handle(this.addToken(request, token));
          }

          return throwError(() => new Error('No token received.'));
        }));
    }
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }
}
